// QuickStudyWindow — wrapper do pop-out (?view=quickstudy-window).
//
// Layout dedicado pra janela pequena: header enxuto, drawer lateral pra
// conversas (escondido por padrão), chat ocupando o espaço todo e composer
// compacto no fim. Quando não há conversa ativa, mostra um onboarding
// curto + botão "Nova dúvida" em destaque (em vez de só uma mensagem cinza).

import { useEffect, useState } from "preact/hooks";
import {
  Archive,
  ArchiveRestore,
  Loader2,
  MessageCirclePlus,
  Pencil,
  Sparkles,
  Trash2,
  X
} from "lucide-react";
import {
  archiveQuickStudyConversation,
  deleteQuickStudyConversation,
  loadQuickStudyConversations,
  loadQuickStudyLimits,
  newQuickStudyConversation,
  openQuickStudyConversation,
  quickStudyActiveId,
  quickStudyConversations,
  quickStudyListMode,
  renameQuickStudyConversation,
  setQuickStudyListMode
} from "./quickStudyStore";
import { QuickStudyChat, QuickStudyComposer } from "./QuickStudy";
import { openConfirm, openPrompt } from "./ConfirmPromptModal";

export function QuickStudyWindow() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    void loadQuickStudyLimits();
    void loadQuickStudyConversations();
    try {
      const convId = new URL(window.location.href).searchParams.get("convId");
      if (convId) void openQuickStudyConversation(convId);
    } catch {
      /* ignore */
    }
    document.title = "Tira-dúvida — Capelo";
  }, []);

  const convs = quickStudyConversations.value;
  const activeId = quickStudyActiveId.value;
  const active = convs.find((c) => c.id === activeId);
  const listMode = quickStudyListMode.value;

  async function handleNew() {
    if (listMode === "archived") setQuickStudyListMode("active");
    const conv = await newQuickStudyConversation();
    setDrawerOpen(false);
    return conv;
  }

  function pickConversation(id: string) {
    void openQuickStudyConversation(id);
    setDrawerOpen(false);
  }

  async function handleDelete(id: string) {
    const ok = await openConfirm({
      title: "Excluir conversa?",
      message: "Todas as mensagens dessa conversa serão removidas.",
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    await deleteQuickStudyConversation(id);
  }

  async function handleRename(id: string, currentTitle: string) {
    const v = await openPrompt({
      title: "Renomear conversa",
      message: "Novo título da conversa",
      placeholder: "Nome curto",
      defaultValue: currentTitle
    });
    if (v && v.trim()) await renameQuickStudyConversation(id, v.trim());
  }

  return (
    <div class="flex h-screen w-screen flex-col bg-background">
      {/* Header enxuto */}
      <header class="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-card/40 px-3 py-2">
        <div class="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir conversas"
            title="Histórico de conversas"
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium hover:border-violet-500"
          >
            <MessageCirclePlus className="size-3.5" />
            <span class="hidden sm:inline">Conversas</span>
            {convs.length > 0 && (
              <span class="rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold text-violet-700 dark:text-violet-300">
                {convs.length}
              </span>
            )}
          </button>
          <div class="flex min-w-0 items-center gap-1.5">
            <Sparkles className="size-4 shrink-0 text-violet-600" />
            <span class="truncate text-sm font-bold">
              {active?.title ?? "Tutor IA"}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleNew}
          class="inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-violet-700"
          title="Nova dúvida"
        >
          <MessageCirclePlus className="size-3.5" />
          <span class="hidden sm:inline">Nova</span>
        </button>
      </header>

      {/* Drawer lateral — overlay quando aberto */}
      {drawerOpen && (
        <>
          <div
            class="fixed inset-0 z-40 bg-black/50"
            onClick={() => setDrawerOpen(false)}
          />
          <aside class="fixed inset-y-0 left-0 z-50 flex w-[min(280px,80vw)] flex-col gap-2 border-r border-border bg-card p-2 shadow-2xl">
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Conversas
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Fechar"
                class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleNew}
              class="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
            >
              <MessageCirclePlus className="size-3.5" />
              Nova conversa
            </button>
            <div class="inline-flex gap-0.5 rounded-md border border-border bg-background p-0.5">
              <button
                type="button"
                onClick={() => setQuickStudyListMode("active")}
                class={`flex-1 rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                  listMode === "active"
                    ? "bg-violet-600 text-white"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                Ativas
              </button>
              <button
                type="button"
                onClick={() => setQuickStudyListMode("archived")}
                class={`flex-1 rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                  listMode === "archived"
                    ? "bg-violet-600 text-white"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                Arquivadas
              </button>
            </div>
            <div class="-mr-1 flex-1 overflow-y-auto pr-1">
              {convs.length === 0 && (
                <div class="px-1 py-4 text-center text-[11px] text-muted-foreground">
                  {listMode === "archived"
                    ? "Nenhuma conversa arquivada."
                    : "Nenhuma conversa ainda."}
                </div>
              )}
              {convs.map((c) => (
                <div
                  key={c.id}
                  class={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-xs transition-colors ${
                    c.id === activeId
                      ? "bg-violet-500/10 text-violet-700 dark:text-violet-300"
                      : "hover:bg-muted"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => pickConversation(c.id)}
                    class="min-w-0 flex-1 truncate text-left"
                    title={c.title}
                  >
                    {c.title}
                  </button>
                  {listMode === "active" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void handleRename(c.id, c.title)}
                        aria-label="Renomear"
                        title="Renomear"
                        class="opacity-0 transition-opacity hover:text-violet-600 group-hover:opacity-100"
                      >
                        <Pencil className="size-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          void archiveQuickStudyConversation(c.id, true)
                        }
                        aria-label="Arquivar"
                        title="Arquivar"
                        class="opacity-0 transition-opacity hover:text-amber-600 group-hover:opacity-100"
                      >
                        <Archive className="size-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(c.id)}
                        aria-label="Excluir"
                        title="Excluir"
                        class="opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          void archiveQuickStudyConversation(c.id, false)
                        }
                        aria-label="Restaurar"
                        title="Restaurar"
                        class="opacity-0 transition-opacity hover:text-emerald-600 group-hover:opacity-100"
                      >
                        <ArchiveRestore className="size-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(c.id)}
                        aria-label="Excluir permanentemente"
                        title="Excluir permanentemente"
                        class="opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </aside>
        </>
      )}

      {/* Chat — ocupa o resto verticalmente. Quando vazio, mostra
          onboarding em vez de placeholder cinza. */}
      <div class="flex flex-1 min-h-0 flex-col">
        {activeId ? (
          <QuickStudyChat compact />
        ) : (
          <EmptyOnboarding onNew={handleNew} />
        )}
      </div>

      {/* Composer compacto no fim */}
      <div class="shrink-0 border-t border-border p-2">
        <QuickStudyComposer
          conversationId={activeId}
          compact
          autoCreate
          autoFocus={!active}
        />
      </div>
    </div>
  );
}

function EmptyOnboarding({ onNew }: { onNew: () => Promise<unknown> }) {
  const [creating, setCreating] = useState(false);
  async function handleClick() {
    setCreating(true);
    try {
      await onNew();
    } finally {
      setCreating(false);
    }
  }
  return (
    <div class="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
      <div class="flex size-12 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg">
        <Sparkles className="size-6" />
      </div>
      <div>
        <h3 class="text-sm font-bold">Tire qualquer dúvida</h3>
        <p class="mt-1 text-[11px] text-muted-foreground">
          Digite a pergunta, anexe foto da prova, print da lista ou PDF.
          Resposta na hora.
        </p>
      </div>
      <button
        type="button"
        onClick={handleClick}
        disabled={creating}
        class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-xs font-bold text-white hover:bg-violet-700 disabled:opacity-50"
      >
        {creating ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <MessageCirclePlus className="size-3.5" />
        )}
        Começar nova dúvida
      </button>
      <p class="text-[10px] text-muted-foreground">
        ou digite direto no campo abaixo
      </p>
    </div>
  );
}
